/**
 * WhatsApp inbox — every message the clinic number receives, threaded by phone number.
 *
 * The 24-hour rule shapes this whole screen: WhatsApp only accepts a free-text reply within 24h of
 * the patient's last message. After that only an approved template will send, so the composer swaps
 * itself out rather than letting staff type something that would be rejected.
 *
 * There's no realtime transport in this app, so the list and the open thread poll. Both pause when
 * the tab is hidden, which is what `skipPollingIfUnfocused` does.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  MessageCircle, Send, Search, Clock, Check, CheckCheck, AlertTriangle,
  Paperclip, User, RefreshCw, MessageSquareText, Inbox,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useListConversationsQuery,
  useListWaMessagesQuery,
  useReplyToConversationMutation,
  useSendConversationTemplateMutation,
  useMarkConversationReadMutation,
} from '../../api/whatsappApi';
import { useListWhatsAppTemplatesQuery } from '../../api/campaignsApi';
import { useGetWhatsAppStatusQuery } from '../../api/communicationsApi';

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short' });
};

const countdown = (expiresAt) => {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt) - Date.now();
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
};

const apiError = (e) => e?.data?.error?.message || 'Something went wrong.';

/** First letter of each of the first two words — same convention as the `.avatar` class elsewhere. */
const initials = (name) => (name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

export default function WhatsAppPage() {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  // On by default — every outbound send (confirmations, reminders, campaigns) auto-creates a
  // conversation, so without this filter the inbox fills with one-way threads nobody ever
  // answered and pushes real replies out of view. Toggleable for staff who want the full list.
  const [repliedOnly, setRepliedOnly] = useState(true);

  const { data: waStatus } = useGetWhatsAppStatusQuery();
  const { data: conversations, isFetching: listLoading, refetch } = useListConversationsQuery(
    { page: 1, pageSize: 50, search, repliedOnly },
    { pollingInterval: 10000, skipPollingIfUnfocused: true }
  );

  // Memoised so the fallback [] isn't a fresh array on every render, which would re-fire the
  // effect below on each poll and fight the user's selection.
  const items = useMemo(() => conversations?.items ?? [], [conversations]);
  const selected = useMemo(() => items.find((c) => c.id === selectedId) ?? null, [items, selectedId]);

  // Default to the top conversation once the list first loads.
  useEffect(() => {
    if (!selectedId && items.length > 0) setSelectedId(items[0].id);
  }, [items, selectedId]);

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb"><span>Home</span><span className="bc-sep">/</span><span className="bc-cur">WhatsApp</span></div>
        <div className="page-header-row">
          <div>
            <h1>WhatsApp</h1>
            <div className="page-header-sub">Replies from patients and leads, in one place.</div>
          </div>
          <button className="btn btn-outline" onClick={refetch} disabled={listLoading}>
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      {waStatus?.paused && (
        <div className="maintenance-banner" role="status">
          <AlertTriangle size={16} />
          <span>
            <strong>WhatsApp sending is paused.</strong> Incoming messages still arrive and are recorded —
            but replies won’t go out until sending is switched back on.
          </span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 360px) 1fr', gap: 14, alignItems: 'start' }}>
        <div className="card" style={{ overflow: 'hidden', boxShadow: 'var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.06))' }}>
          <div style={{ padding: 10, borderBottom: '1px solid var(--border)', position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              className="form-control"
              style={{ paddingLeft: 30 }}
              placeholder="Search name or number…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div style={{ display: 'flex', gap: 4, padding: '8px 10px', borderBottom: '1px solid var(--border)', background: 'var(--bg-subtle, var(--gray-50))' }}>
            <SegButton active={repliedOnly} icon={MessageSquareText} label="Replied" onClick={() => setRepliedOnly(true)} />
            <SegButton active={!repliedOnly} icon={Inbox} label="All" onClick={() => setRepliedOnly(false)} />
          </div>

          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {listLoading && items.length === 0 && <div className="skeleton" style={{ height: 64, margin: 10 }} />}
            {items.map((c) => (
              <ConversationRow key={c.id} c={c} active={c.id === selectedId} onSelect={() => setSelectedId(c.id)} />
            ))}
            {!listLoading && items.length === 0 && (
              <div className="empty-state" style={{ padding: 20 }}>
                <MessageCircle className="es-icon" size={32} />
                <h3>{repliedOnly ? 'No replies yet' : 'No conversations yet'}</h3>
                <p>
                  {repliedOnly
                    ? <>Nobody has replied to the clinic's WhatsApp number yet. Switch to <strong>All</strong> to see every conversation, including ones only sent to.</>
                    : "Messages appear here as soon as a confirmation, reminder or campaign goes out."}
                </p>
              </div>
            )}
          </div>
        </div>

        {selected
          ? <ThreadPane key={selected.id} conversation={selected} />
          : (
            <div className="card card-body">
              <div className="empty-state">
                <MessageCircle className="es-icon" size={32} />
                <h3>Pick a conversation</h3>
                <p>Select a chat on the left to read it and reply.</p>
              </div>
            </div>
          )}
      </div>
    </>
  );
}

/** Small pill toggle — used for the Replied/All list filter. */
function SegButton({ active, icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
        padding: '5px 8px', fontSize: '0.78rem', fontWeight: 700, border: 0, borderRadius: 'var(--r-md, 6px)',
        cursor: 'pointer', transition: 'background 0.15s, color 0.15s',
        background: active ? 'var(--bg-card, #fff)' : 'transparent',
        color: active ? 'var(--primary, #2563eb)' : 'var(--text-muted)',
        boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
      }}
    >
      <Icon size={13} /> {label}
    </button>
  );
}

function ConversationRow({ c, active, onSelect }) {
  const left = countdown(c.sessionExpiresAt);
  return (
    <button
      onClick={onSelect}
      style={{
        display: 'flex', gap: 10, width: '100%', textAlign: 'left', padding: '10px 12px', border: 0,
        borderLeft: `3px solid ${active ? 'var(--primary, #2563eb)' : 'transparent'}`,
        borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.12s',
        background: active ? 'var(--bg-hover)' : 'transparent',
      }}
    >
      <div className="avatar av-md" style={{ marginTop: 1 }}>{initials(c.displayName)}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span className="td-bold" style={{ fontSize: '0.86rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.displayName}</span>
          <span className="td-muted" style={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{fmtTime(c.lastMessageAt)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginTop: 2 }}>
          <span className="td-muted" style={{ fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {c.lastMessagePreview || '—'}
          </span>
          {c.unreadCount > 0 && <span className="badge b-green" style={{ flexShrink: 0 }}>{c.unreadCount}</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
          {!c.patientId && !c.leadId && <span className="badge b-gray" title="Not linked to a patient or lead">Unknown</span>}
          {c.patientCode && <span className="badge b-blue">{c.patientCode}</span>}
          {c.sessionOpen
            ? <span className="badge b-green" title="A free-text reply is allowed"><Clock size={9} /> {left}</span>
            : <span className="badge b-amber" title="Only an approved template can be sent">Template only</span>}
        </div>
      </div>
    </button>
  );
}

function ThreadPane({ conversation }) {
  const [text, setText] = useState('');
  const [templateId, setTemplateId] = useState('');

  const { data: messages, isFetching } = useListWaMessagesQuery(
    { id: conversation.id, page: 1, pageSize: 100 },
    { pollingInterval: 5000, skipPollingIfUnfocused: true }
  );
  // Only variable-free approved templates can be sent from here until the automation engine can
  // resolve variables against live CRM data.
  const { data: templates } = useListWhatsAppTemplatesQuery({ usableOnly: true });
  const sendable = (templates ?? []).filter((t) => (t.variables ?? []).length === 0);

  const [reply, { isLoading: replying }] = useReplyToConversationMutation();
  const [sendTemplate, { isLoading: sendingTemplate }] = useSendConversationTemplateMutation();
  const [markRead] = useMarkConversationReadMutation();

  useEffect(() => {
    if (conversation.unreadCount > 0) markRead(conversation.id);
  }, [conversation.id, conversation.unreadCount, markRead]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      await reply({ id: conversation.id, text }).unwrap();
      setText('');
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sendTpl = async () => {
    if (!templateId) return;
    try {
      await sendTemplate({ id: conversation.id, templateId }).unwrap();
      setTemplateId('');
      toast.success('Template sent.');
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const left = countdown(conversation.sessionExpiresAt);

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', height: '70vh', boxShadow: 'var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.06))' }}>
      <div className="card-header">
        <div className="card-header-left" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="avatar av-md">{initials(conversation.displayName)}</div>
          <div>
            <h3 style={{ lineHeight: 1.2 }}>{conversation.displayName}</h3>
            <span className="td-mono td-muted" style={{ fontSize: '0.78rem' }}>+{conversation.normalizedPhone}</span>
          </div>
        </div>
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {conversation.patientId && <span className="badge b-blue"><User size={10} /> Patient</span>}
          {conversation.leadId && !conversation.patientId && <span className="badge b-purple"><User size={10} /> Lead</span>}
          {conversation.sessionOpen
            ? <span className="badge b-green"><Clock size={10} /> {left}</span>
            : <span className="badge b-amber">Window closed</span>}
        </span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 9, background: 'var(--bg-subtle, var(--gray-50))' }}>
        {isFetching && !messages && <div className="skeleton" style={{ height: 40 }} />}
        {(messages?.items ?? []).map((m) => <Bubble key={m.id} m={m} />)}
        {messages?.items?.length === 0 && (
          <div className="td-muted" style={{ textAlign: 'center', fontSize: '0.82rem', marginTop: 20 }}>No messages in this chat yet.</div>
        )}
      </div>

      <div style={{ borderTop: '1px solid var(--border)', padding: 10 }}>
        {conversation.sessionOpen ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            {/* A plain <input> is single-line by construction — Shift+Enter has nowhere to put a
                line break, so it could never do anything but look like a no-op right before Enter
                sent the message. A <textarea> is what actually makes "newline on Shift+Enter" possible. */}
            <textarea
              className="form-control"
              placeholder="Type a reply… (Shift+Enter for a new line)"
              rows={1}
              style={{ resize: 'none', maxHeight: 120, overflowY: 'auto' }}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                // Shift+Enter: no preventDefault — the textarea's own default behavior inserts the newline.
              }}
            />
            <button className="btn btn-primary" onClick={send} disabled={replying || !text.trim()}>
              <Send size={15} /> Send
            </button>
          </div>
        ) : (
          <div>
            <div className="td-muted" style={{ fontSize: '0.78rem', marginBottom: 6, display: 'flex', gap: 6 }}>
              <AlertTriangle size={13} />
              This chat has been quiet for over 24 hours, so WhatsApp no longer allows a free-text reply.
              Send an approved template to reopen it.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <select className="form-control" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                <option value="">Choose an approved template…</option>
                {sendable.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <button className="btn btn-primary" onClick={sendTpl} disabled={sendingTemplate || !templateId}>
                <Send size={15} /> Send
              </button>
            </div>
            {sendable.length === 0 && (
              <div className="td-muted" style={{ fontSize: '0.74rem', marginTop: 4 }}>
                No approved variable-free templates yet — create one under Campaigns.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Bubble({ m }) {
  const outbound = m.direction === 'Outbound';
  return (
    <div style={{ alignSelf: outbound ? 'flex-end' : 'flex-start', maxWidth: '72%' }}>
      <div
        style={{
          background: outbound ? 'var(--green-100, #dcfce7)' : 'var(--bg-card, #fff)',
          border: outbound ? '1px solid var(--green-200, #bbf7d0)' : '1px solid var(--border)',
          borderRadius: 12,
          borderBottomRightRadius: outbound ? 3 : 12,
          borderBottomLeftRadius: outbound ? 12 : 3,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          padding: '8px 11px',
          fontSize: '0.85rem',
          whiteSpace: 'pre-wrap',
        }}
      >
        {m.hasMedia && (
          <div className="td-muted" style={{ fontSize: '0.76rem', display: 'flex', gap: 4, marginBottom: 3 }}>
            <Paperclip size={12} /> {m.mediaFileName || m.type}
          </div>
        )}
        {m.body || m.caption || <span className="td-muted">[{m.type}]</span>}
      </div>
      <div className="td-muted" style={{ fontSize: '0.68rem', marginTop: 2, textAlign: outbound ? 'right' : 'left', display: 'flex', gap: 4, justifyContent: outbound ? 'flex-end' : 'flex-start' }}>
        <span>{fmtTime(m.createdAt)}</span>
        {outbound && <Ticks status={m.status} />}
        {m.errorMessage && <span style={{ color: 'var(--danger)' }} title={m.errorMessage}>failed</span>}
      </div>
    </div>
  );
}

/** One tick sent, two delivered, two coloured read — matching what people expect from WhatsApp. */
function Ticks({ status }) {
  if (status === 'Read') return <CheckCheck size={12} style={{ color: 'var(--primary, #2563eb)' }} />;
  if (status === 'Delivered') return <CheckCheck size={12} />;
  if (status === 'Sent') return <Check size={12} />;
  if (status === 'Failed') return <AlertTriangle size={12} style={{ color: 'var(--danger)' }} />;
  return <Clock size={11} />;
}
